import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useRestorePassword } from '../../../hooks/auth/use-restore-password';
import { useTranslations } from '../../../hooks/use-translations';
import { TranslateFunction } from '../../../i18n/types/next-intl';
import { ModalDialog } from '../../common';

import styles from './RestorePasswordModal.module.scss';

type RestorePasswordModalProps = {
  open: boolean;
  onClose(): void;
};

type RestorePasswordForm = {
  email: string;
};

const getRestorePasswordSchema = (t: TranslateFunction<'errors'>) =>
  yup
    .object({
      email: yup.string().email(t('incorrect format')).required(t('field is required')),
    })
    .required();

export const RestorePasswordModal: FC<RestorePasswordModalProps> = (props) => {
  const { open, onClose } = props;
  const { registerRestorePassword } = useRestorePassword();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const t = useTranslations('pages.account.login.restorePassword');
  const tError = useTranslations('errors');
  const restorePasswordSchema = useMemo(() => getRestorePasswordSchema(tError), [tError]);
  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RestorePasswordForm>({
    resolver: yupResolver(restorePasswordSchema),
    mode: 'onSubmit',
  });

  const onSubmit = useCallback(
    async (params: RestorePasswordForm) => {
      const result = await registerRestorePassword({
        email: params.email,
      });

      setFormSubmitted(true);
    },
    [registerRestorePassword]
  );

  useEffect(() => {
    if (!open) {
      reset();
      setFormSubmitted(false);
    }
  }, [open, reset]);

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      title={t('title')}
      styleOptions={{ width: 'calc(100% - 32px)', minWidth: '320px', maxWidth: '420px' }}
      okButton={
        formSubmitted
          ? {
              text: t('form.buttons.ok'),
              onClick: onClose,
            }
          : {
              text: t('form.buttons.send'),
              onClick: handleSubmit(onSubmit),
            }
      }
      cancelButton={{
        text: t('form.buttons.cancel'),
        onClick: onClose,
      }}
    >
      {formSubmitted ? (
        <span>{t('form.success')}</span>
      ) : (
        <>
          <span>{t('description')}</span>

          <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <TextField
              label={t('form.fields.email.label')}
              placeholder={t('form.fields.email.placeholder')}
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              {...register('email')}
            />
          </form>
        </>
      )}
    </ModalDialog>
  );
};
